/**
 * Order note types (PD-14917).
 *
 * `order_note.type` is a varchar holding these numeric strings. The labels
 * used to be hardcoded in five separate places — the order Notes tab, the
 * cart sidebar, the invoice sidebar, the quote sidebar and a skeleton — which
 * is how they drifted apart and how it became unclear which of them a customer
 * could see. They are defined once here instead.
 *
 * The distinction that matters is `isCustomerVisible`. Production content
 * across thirteen tenants shows the three original types are used for internal
 * collaboration — pricing decisions, margin discussion, named competitors,
 * approvals naming individuals, and in one tenant direct criticism of a named
 * employee. None of them may ever reach a customer document. CUSTOMER_FACING
 * exists so there is one channel that can.
 */
export enum OrderNoteType {
	/** Internal working notes. Never shown to a customer. */
	INTERNAL = '1',
	/** Instructions for the internal operations / fulfilment team. */
	OPERATIONS = '2',
	/** Instructions for the driver making the delivery. */
	DELIVERY = '3',
	/** The only note a customer ever sees. Printed on the invoice. */
	CUSTOMER_FACING = '4',
}

export interface OrderNoteTypeDefinition {
	type: OrderNoteType;
	/** Label shown to internal users, in the CRM. */
	label: string;
	/** Short guidance rendered beneath the field at the point of entry. */
	help: string;
	/**
	 * Heading used on customer-facing documents. Only meaningful when
	 * `isCustomerVisible` is true — a customer should not be shown our internal
	 * taxonomy, so the invoice simply calls it "Order Notes".
	 */
	documentHeading?: string;
	isCustomerVisible: boolean;
}

export const ORDER_NOTE_TYPES: ReadonlyArray<OrderNoteTypeDefinition> = [
	{
		type: OrderNoteType.INTERNAL,
		label: 'Order Notes (Internal)',
		help: 'Only visible to your team. Never shown to the customer.',
		isCustomerVisible: false,
	},
	{
		type: OrderNoteType.OPERATIONS,
		label: 'Operations Instructions',
		help: 'Instructions for the operations team handling this order.',
		isCustomerVisible: false,
	},
	{
		type: OrderNoteType.DELIVERY,
		label: 'Delivery Instructions',
		help: 'Shown to the driver at the delivery stop.',
		isCustomerVisible: false,
	},
	{
		type: OrderNoteType.CUSTOMER_FACING,
		label: 'Customer-Facing Note',
		help: 'The customer will see this. Printed on the invoice as "Order Notes".',
		documentHeading: 'Order Notes',
		isCustomerVisible: true,
	},
];

const BY_TYPE = new Map<string, OrderNoteTypeDefinition>(
	ORDER_NOTE_TYPES.map((definition) => [definition.type, definition]),
);

/**
 * Definition for a stored `order_note.type`, or undefined when the value is
 * not one we recognise. Historic rows can carry unexpected values, so callers
 * must handle the undefined case rather than assume.
 */
export function getOrderNoteTypeDefinition(
	type: string | null | undefined,
): OrderNoteTypeDefinition | undefined {
	return type != null ? BY_TYPE.get(type) : undefined;
}

/** Internal label for a stored type, falling back to the raw value. */
export function getOrderNoteLabel(type: string | null | undefined): string {
	return getOrderNoteTypeDefinition(type)?.label ?? `Unknown Type (${type})`;
}

/** True only for the one type a customer is allowed to see. */
export function isCustomerVisibleOrderNote(
	type: string | null | undefined,
): boolean {
	return getOrderNoteTypeDefinition(type)?.isCustomerVisible === true;
}

/**
 * Longest label a tenant may give a note type (PD-15892). Matches the
 * `order_note_type_config.labelOverride` column.
 */
export const ORDER_NOTE_TYPE_LABEL_MAX_LENGTH = 100;

/**
 * One tenant override row, as `order_note_type_config` stores it (PD-15892).
 * `isVisible` is typed `boolean | number` on purpose: it is a MySQL tinyint,
 * which TypeORM hydrates as the number 0 or 1, so the resolver coerces it.
 */
export interface OrderNoteTypeConfigRowModel {
	noteType: string;
	isVisible: boolean | number;
	labelOverride: string | null;
	sortOrder: number | null;
}

/**
 * A note type as this tenant's CRM shows it: the code definition with the
 * tenant's overrides applied (PD-15892).
 */
export interface ResolvedOrderNoteTypeModel {
	type: OrderNoteType;
	/** The label to show in the CRM: the tenant's name when set, else the default. */
	label: string;
	/** The code label. The settings screen uses it as the placeholder. */
	defaultLabel: string;
	/** The tenant's own name, or null when the default applies. */
	labelOverride: string | null;
	help: string;
	/** Whether the CRM offers the type for new notes. */
	isVisible: boolean;
	/** Zero-based position, after the tenant's reordering. */
	sortOrder: number;
	isCustomerVisible: boolean;
}

/**
 * A label override as it should be stored: trimmed, and null when it is empty
 * or the same as the default label, so a "rename" back to the default leaves
 * no override behind.
 */
export function normalizeOrderNoteLabelOverride(
	labelOverride: string | null | undefined,
	defaultLabel: string,
): string | null {
	const trimmed = labelOverride?.trim() ?? '';
	if (trimmed === '' || trimmed === defaultLabel) {
		return null;
	}
	return trimmed;
}

/**
 * Apply a tenant's overrides to the code definitions (PD-15892).
 *
 * Every code type is always returned, so a screen can render the full list
 * with no rows at all. A type without a row keeps its code label, stays
 * visible and keeps its code position; rows for values the code does not know
 * are ignored. The result is sorted by the effective position, ties broken by
 * code order, so a partial or inconsistent set of rows still gives a stable
 * order.
 */
export function resolveOrderNoteTypes(
	rows: ReadonlyArray<OrderNoteTypeConfigRowModel>,
): Array<ResolvedOrderNoteTypeModel> {
	const byType = new Map<string, OrderNoteTypeConfigRowModel>();
	for (const row of rows) {
		byType.set(row.noteType, row);
	}

	return ORDER_NOTE_TYPES.map((definition, codeIndex) => {
		const row = byType.get(definition.type);
		const labelOverride =
			row != null
				? normalizeOrderNoteLabelOverride(row.labelOverride, definition.label)
				: null;
		const sortOrder =
			row?.sortOrder != null && Number.isFinite(Number(row.sortOrder))
				? Number(row.sortOrder)
				: codeIndex;
		return {
			resolved: {
				type: definition.type,
				label: labelOverride ?? definition.label,
				defaultLabel: definition.label,
				labelOverride,
				help: definition.help,
				// Coerce the tinyint 0/1 (see OrderNoteTypeConfigRowModel).
				isVisible: row != null ? Boolean(row.isVisible) : true,
				sortOrder,
				isCustomerVisible: definition.isCustomerVisible,
			},
			codeIndex,
		};
	})
		.sort(
			(a, b) =>
				a.resolved.sortOrder - b.resolved.sortOrder || a.codeIndex - b.codeIndex,
		)
		.map(({ resolved }, position) => ({ ...resolved, sortOrder: position }));
}

/**
 * The label for a stored type using the tenant's resolved list, falling back to
 * the code label (and then to the raw value) for a type the list does not hold.
 */
export function getResolvedOrderNoteLabel(
	resolved: ReadonlyArray<ResolvedOrderNoteTypeModel>,
	type: string | null | undefined,
): string {
	return (
		resolved.find((entry) => entry.type === type)?.label ??
		getOrderNoteLabel(type)
	);
}
